import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/src/renderer/components/ui/Button'
import './ShortcutInput.scss'

/**
 * Tasten, die für sich allein kein Kürzel ergeben. Ein Kürzel aus einer
 * nackten Taste würde jedes Tippen im ganzen System abfangen.
 */
const NUR_MODIFIKATOR = new Set(['Control', 'Shift', 'Alt', 'Meta'])

/**
 * Ein `keydown` in die Schreibweise übersetzen, die Electrons
 * `globalShortcut` versteht (ein „Accelerator": `Alt+Shift+N`).
 *
 * Gibt `null` zurück, wenn daraus kein brauchbares Kürzel wird — nur
 * Modifikatoren, oder gar kein Modifikator. Ohne Modifikator wäre es
 * **systemweit** jede einzelne Taste; das darf man niemandem einstellen
 * lassen.
 */
export function acceleratorAusEreignis(e: {
  key: string
  code: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
}): string | null {
  if (NUR_MODIFIKATOR.has(e.key)) return null

  const teile: string[] = []
  if (e.metaKey) teile.push('CommandOrControl')
  if (e.ctrlKey && !e.metaKey) teile.push('CommandOrControl')
  if (e.altKey) teile.push('Alt')
  if (e.shiftKey) teile.push('Shift')
  if (teile.length === 0) return null

  // `code` statt `key`: Mit gedrückter Alt-Taste liefert `key` auf dem Mac
  // Sonderzeichen („…" statt „;"), und Electron kennt die nicht.
  let taste: string
  if (e.code.startsWith('Key')) taste = e.code.slice(3)
  else if (e.code.startsWith('Digit')) taste = e.code.slice(5)
  else if (e.code === 'Space') taste = 'Space'
  else if (e.code.startsWith('F') && /^F\d+$/.test(e.code)) taste = e.code
  else return null

  teile.push(taste)
  return teile.join('+')
}

/** Den Accelerator so schreiben, wie er auf der Tastatur steht. */
export function acceleratorLesbar(accelerator: string | null, mac: boolean): string {
  if (!accelerator) return ''
  return accelerator
    .split('+')
    .map(teil => {
      if (teil === 'CommandOrControl') return mac ? '⌘' : 'Strg'
      if (teil === 'Alt') return mac ? '⌥' : 'Alt'
      if (teil === 'Shift') return mac ? '⇧' : 'Umschalt'
      if (teil === 'Space') return mac ? '␣' : 'Leertaste'
      return teil
    })
    .join(mac ? '' : '+')
}

export interface ShortcutInputProps {
  /** Das aktuelle Kürzel als Electron-Accelerator, `null` = keines. */
  value: string | null
  /** Die aufgenommene Kombination. Gibt zurück, ob sie angenommen wurde. */
  onValueChange: (accelerator: string | null) => Promise<boolean> | boolean
  mac?: boolean
  id?: string
}

/**
 * Ein Feld, das eine **Tastenkombination** aufnimmt (Abnahme F-Q4).
 *
 * Es ist kein Textfeld: Man klickt hinein und drückt die Kombination. Was
 * dabei herauskommt, muss Electrons `globalShortcut` verstehen — deshalb die
 * Übersetzung in {@link acceleratorAusEreignis} statt roher Tastennamen.
 *
 * **Ein abgelehntes Kürzel bleibt sichtbar.** `onValueChange` gibt zurück, ob
 * es angenommen wurde; bei `false` sagt das Feld es und behält das alte. Eine
 * Einstellung, die stillschweigend nichts tut, wäre schlimmer als keine.
 */
export const ShortcutInput: React.FC<ShortcutInputProps> = ({ value, onValueChange, mac = true, id }) => {
  const { t } = useTranslation()
  const [nimmtAuf, setNimmtAuf] = useState(false)
  const [abgelehnt, setAbgelehnt] = useState(false)

  const handleKeyDown = useCallback(async (e: React.KeyboardEvent) => {
    if (!nimmtAuf) return
    e.preventDefault()
    e.stopPropagation()

    if (e.key === 'Escape') {
      setNimmtAuf(false)
      return
    }
    const accelerator = acceleratorAusEreignis(e)
    if (!accelerator) return

    setNimmtAuf(false)
    const angenommen = await onValueChange(accelerator)
    setAbgelehnt(!angenommen)
  }, [nimmtAuf, onValueChange])

  const handleClear = useCallback(async () => {
    setAbgelehnt(false)
    setNimmtAuf(false)
    await onValueChange(null)
  }, [onValueChange])

  return (
    <div className="shortcut-input">
      <button
        type="button"
        id={id}
        className={`shortcut-input__field${nimmtAuf ? ' shortcut-input__field--recording' : ''}`}
        onClick={() => { setNimmtAuf(true); setAbgelehnt(false) }}
        onBlur={() => setNimmtAuf(false)}
        onKeyDown={handleKeyDown}
        aria-label={t('preferences.shortcut.aria')}
      >
        {nimmtAuf
          ? t('preferences.shortcut.recording')
          : value
            ? acceleratorLesbar(value, mac)
            : t('preferences.shortcut.none')}
      </button>

      {value && !nimmtAuf && (
        <Button variant="tertiary" size="sm" onClick={handleClear}>
          {t('preferences.shortcut.clear')}
        </Button>
      )}

      {abgelehnt && (
        <p className="shortcut-input__rejected" role="alert">
          {t('preferences.shortcut.taken')}
        </p>
      )}
    </div>
  )
}
